import { FC, lazy, Suspense } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useSelector } from 'react-redux';

import { ITEM_LEAD_TIME_AVAILABLE_NOW } from '../../constants/itemConstants';
import { PdpSharedLeadTime } from '../PdpSharedLeadTime/PdpSharedLeadTime';
import { PdpSharedBlurb } from './PdpSharedBlurb/PdpSharedBlurb';

const ClockIcon = lazy(
    () =>
        import(/* webpackChunkName: "PdpSharedBlurbLeadTimeClockIcon" */ 'dibs-icons/exports/Clock')
);

import { PdpSharedBlurbLeadTime_sku$key } from './__generated__/PdpSharedBlurbLeadTime_sku.graphql';
import { PdpSharedBlurbLeadTime_item$key } from './__generated__/PdpSharedBlurbLeadTime_item.graphql';

const skuFragment = graphql`
    fragment PdpSharedBlurbLeadTime_sku on ItemSkuType {
        leadTime(pageType: PDP)
    }
`;

const itemFragment = graphql`
    fragment PdpSharedBlurbLeadTime_item on Item {
        itemType
        isNewAndMadeToOrder
        isListingUnlisted
        attributes {
            leadTime {
                option {
                    name
                    urlLabel
                }
            }
        }
    }
`;

// TODO: replace with State type
type ReduxState = {
    relayVariables: {
        variables: {
            isMultiSku: boolean;
            isClient: boolean;
        };
    };
};

const Icon: FC = () => {
    const { isClient } = useSelector((state: ReduxState) => state.relayVariables.variables);

    return isClient ? (
        <Suspense fallback={null}>
            <ClockIcon />
        </Suspense>
    ) : null;
};

export const PdpSharedBlurbLeadTime: FC<{
    sku?: PdpSharedBlurbLeadTime_sku$key | null;
    item: PdpSharedBlurbLeadTime_item$key | null;
    hideIcon: boolean;
}> = ({ hideIcon = false, ...props }) => {
    const sku = useFragment(skuFragment, props.sku || null);
    const item = useFragment(itemFragment, props.item);

    const { isMultiSku } = useSelector((state: ReduxState) => state.relayVariables.variables);

    const isNewAndMadeToOrder = item?.isNewAndMadeToOrder;
    const isNewAndCustom = item?.itemType === 'NEW_MADE_TO_ORDER';
    const isListingUnlisted = item?.isListingUnlisted;

    const option = item?.attributes?.leadTime?.[0]?.option;
    const leadTimeUrlLabel = option?.urlLabel;
    const isAvailableNow = leadTimeUrlLabel === ITEM_LEAD_TIME_AVAILABLE_NOW;

    let leadTime: string | null | undefined;
    if (
        !isNewAndCustom &&
        !isMultiSku &&
        leadTimeUrlLabel &&
        !isAvailableNow &&
        isNewAndMadeToOrder
    ) {
        leadTime = option?.name;
    } else if (isNewAndCustom && !isListingUnlisted) {
        leadTime = sku?.leadTime;
    }

    return leadTime ? (
        <PdpSharedBlurb hideIcon={hideIcon} IconComponent={Icon} dataTn="pdp-blurb-lead-time">
            <PdpSharedLeadTime leadTime={leadTime} />
        </PdpSharedBlurb>
    ) : null;
};
